import { m } from "@repo/i18n";
import { PLURAL_SELECTORS } from "./plural-selectors";
/**
 * The lookup table for RUNTIME keys, built from the module namespace.
 *
 * ── THIS IS THE ONE PLACE `m` IS READ BY STRING. ───────────────────────────
 * Holding the whole namespace defeats tree-shaking for every message, which is
 * the price of a key the server chooses (`D27`). Screens import their messages
 * statically and call them directly; only code whose key arrives as data comes
 * through here.
 */
const MESSAGES = m;
/**
 * What an unrecognised key renders as.
 *
 * ── AN UNKNOWN KEY IS NORMAL, NOT EXCEPTIONAL. ───────────────────────────────
 * The Worker deploys in seconds and the app arrives over OTA whenever the phone
 * next opens it — so "the server emits a key this build has never heard of" is
 * the ordinary state of every deploy, not an edge case. It therefore may not
 * render as the raw key (`otp.invalid_code` on screen is a leaked internal
 * identifier and reads as a crash), nor as an empty string (a dialog with no
 * text, or a screen that looks frozen).
 *
 * Paraglide on its own returns the RAW KEY for a message it does not have,
 * which is exactly the first of those. That is why this façade exists rather
 * than callers indexing `m` themselves.
 *
 * It renders as `error.unrecognized` in the ACTIVE locale — a real sentence, in
 * the user's own language, naming the remedy that actually fixes it. The cost
 * is that specificity is lost for that one message; the alternative,
 * prettifying the key into "Invalid code", produces English text inside a
 * Portuguese app and quietly encodes the assumption that keys are always
 * readable English, which nothing enforces.
 */
export const FALLBACK_KEY = "error.unrecognized";
/**
 * A translator bound to one locale.
 *
 * `docs/domain/GLOSSARY.md` states the fallback rule as three levels —
 * localized text, then English, then the canonical value. Here the middle level
 * is Paraglide's: a message missing from pt-BR compiles to its en-US text, and
 * `@repo/i18n`'s parity test refuses a key present in one locale file and not
 * the other. What is left for runtime is the level that genuinely cannot be
 * checked — a key that exists on the server and in no bundle at all.
 *
 * The locale of every call is the translator's own, passed explicitly. It never
 * comes from Paraglide's global `getLocale()`, so a translator built for pt-BR
 * renders pt-BR whatever the rest of the app is doing.
 *
 * (The rule still applies as written to catalogue TEXT, which comes from the
 * database and can be missing a translation row.)
 */
export function createTranslator(locale, options = {}) {
    const translator = (key, params) => {
        if (!hasMessage(key)) {
            options.onFallback?.(key, "unknown-key");
            return fallback(locale);
        }
        const rendered = render(key, locale, params);
        if (rendered === null) {
            // The key exists but the server sent no value for a placeholder it
            // needs. Paraglide would interpolate it as `undefined`, which is the
            // thing this whole file refuses to put on screen.
            options.onFallback?.(key, "missing-params");
            return fallback(locale);
        }
        return rendered;
    };
    return Object.assign(translator, { locale });
}
/** One-shot translation, for code with no translator in hand. */
export function translate(locale, key, params) {
    return createTranslator(locale)(key, params);
}
/**
 * Whether a key is in this build at all. `Object.hasOwn`, not `key in m`:
 * `toString` and `constructor` are not messages.
 */
export function hasMessage(key) {
    return Object.hasOwn(MESSAGES, key);
}
/**
 * Render one known message, or `null` when a parameter it needs did not
 * arrive.
 *
 * ── HOW "NEEDS" IS DETECTED ─────────────────────────────────────────────────
 * The compiled function reads each placeholder as `i?.name`, and only the ones
 * the branch it takes actually uses. So the inputs go in behind a `Proxy` that
 * notes every read of a name whose value is absent — the answer is exact per
 * call, for every namespace, with no list to keep in step with the messages.
 * A plural selector is read before any branch is chosen, so a missing count is
 * caught the same way.
 *
 * What a read cannot reveal is a count that ARRIVED but is not a number
 * (`"soon"`): Paraglide would render it into the `*` form. The old renderer
 * refused that, so {@link PLURAL_SELECTORS} names the selectors and a
 * non-finite one counts as missing.
 */
function render(key, locale, params) {
    const source = params ?? {};
    for (const selector of PLURAL_SELECTORS[key] ?? []) {
        if (!isFiniteCount(source[selector])) {
            return null;
        }
    }
    let missing = false;
    const inputs = new Proxy(source, {
        get(target, name) {
            if (typeof name !== "string") {
                return Reflect.get(target, name);
            }
            const value = Object.hasOwn(target, name) ? target[name] : undefined;
            if (value === undefined || value === null) {
                missing = true;
            }
            return value;
        },
    });
    // `MESSAGES[key]` exists: every caller checked `hasMessage` first.
    const rendered = MESSAGES[key](inputs, { locale });
    return missing ? null : rendered;
}
/** `Number(selector)` is what the old renderer applied, so a numeric string counts. */
function isFiniteCount(value) {
    return Number.isFinite(typeof value === "number" ? value : Number(value));
}
function fallback(locale) {
    // `error.unrecognized` has no parameters, deliberately: the sentence that
    // stands in for a broken one cannot itself be broken by a missing value.
    return m[FALLBACK_KEY]({}, { locale });
}
//# sourceMappingURL=catalogue.js.map